import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { useAuth } from '../context/AuthContext'
import SEO from '../components/SEO'
import { useTheme } from '../context/ThemeContext'
import MobileBackButton from '../components/MobileBackButton'
import AddNewDictionaryEntryModal from '../components/AddNewDictionaryEntryModal'
import HaitianDictionarySyncModal from '../components/HaitianDictionarySyncModal'
import { useAlert } from '../components/AlertModal'
import { useWebSocketUpdates } from '../hooks/useWebSocketUpdates'

export default function HaitianDictionaryPage() {
  const router = useRouter()
  const { isAuthenticated, authToken, isStaff, loading: authLoading } = useAuth()
  const { theme, setTheme } = useTheme() // Subscribe to theme changes for proper re-rendering
  const { showAlert, hideAlert, AlertComponent } = useAlert()
  const isEmbedded = router.query.embedded === 'true'
  const [selectedPOS, setSelectedPOS] = useState(null) // Stores full POS object: { abbreviation, full_name, count }
  const [posCategories, setPOSCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null) // Stores category object: { category, count }
  const [semanticCategories, setSemanticCategories] = useState([])
  const [entries, setEntries] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editingEntry, setEditingEntry] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  // Search filter checkboxes - initialized from localStorage
  const _savedSearchOpts = typeof window !== 'undefined'
    ? (() => { try { return JSON.parse(localStorage.getItem('dictSearchOptions') || '{}') } catch { return {} } })()
    : {}
  const [searchMainWord, setSearchMainWord] = useState(_savedSearchOpts.searchMainWord ?? true)
  const [searchDefinition, setSearchDefinition] = useState(_savedSearchOpts.searchDefinition ?? true)
  const [searchVariants, setSearchVariants] = useState(_savedSearchOpts.searchVariants ?? true)
  const [wordMatch, setWordMatch] = useState(_savedSearchOpts.wordMatch ?? false)
  const [diacriticMatch, setDiacriticMatch] = useState(_savedSearchOpts.diacriticMatch ?? false)
  const [filterWithPronunciation, setFilterWithPronunciation] = useState(_savedSearchOpts.filterWithPronunciation ?? false)
  const [filterNoPronunciation, setFilterNoPronunciation] = useState(_savedSearchOpts.filterNoPronunciation ?? false)
  const [filterWithAudio, setFilterWithAudio] = useState(_savedSearchOpts.filterWithAudio ?? false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrevious, setHasPrevious] = useState(false)
  const [selectedLetter, setSelectedLetter] = useState(null)
  const [availableLetters, setAvailableLetters] = useState([])
  // Debounced search term. `searchTerm` updates on every keystroke; this
  // one lags by 300ms so we only issue one network request per typing
  // burst instead of N. Prevents the async-race pattern where fast
  // typing of "kap" (k → ka → kap) issues three overlapping requests
  // and the slowest (usually the broadest, "k") completes last and
  // silently overwrites the correct "kap" results with mismatched data.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  // Backstop guard for the residual race window if the user pauses long
  // enough between typing bursts that debounce doesn't collapse them.
  // Every fetch stamps itself with an incrementing ID; if the ID no
  // longer matches when the response comes back, the response is stale
  // and gets discarded before setEntries fires. Belt-and-suspenders with
  // the debounce above.
  const latestFetchIdRef = useRef(0)
  const [viewMode, setViewMode] = useState('pos') // 'pos' or 'category'
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [syncStatus, setSyncStatus] = useState(null) // {needs_sync: boolean}
  const syncModalCallbackRef = useRef(null)
  const [dictionaryVersion, setDictionaryVersion] = useState('v4') // Default to v4 (latest)

  // Load dictionary version from localStorage (force v4 if stale)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('dictionaryVersion')
      if (saved && saved !== 'v4' && localStorage.getItem('dictMigrationVersion') !== '2') {
        localStorage.setItem('dictionaryVersion', 'v4')
        localStorage.setItem('dictMigrationVersion', '2')
        setDictionaryVersion('v4')
      } else if (saved) {
        setDictionaryVersion(saved)
      }
    }
  }, [])

  // Listen for storage changes (when version is changed in Global Settings)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'dictionaryVersion' && e.newValue) {
        setDictionaryVersion(e.newValue)
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // Load view mode from localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedViewMode = localStorage.getItem('dictViewMode')
      if (savedViewMode === 'pos' || savedViewMode === 'category') {
        setViewMode(savedViewMode)
      }
    }
  }, [])

  // Save view mode to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('dictViewMode', viewMode)
    }
  }, [viewMode])

  // Save selected POS to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined' && selectedPOS) {
      localStorage.setItem('dictSelectedPOS', JSON.stringify(selectedPOS))
    }
  }, [selectedPOS])

  // Save selected Category to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined' && selectedCategory) {
      localStorage.setItem('dictSelectedCategory', JSON.stringify(selectedCategory))
    }
  }, [selectedCategory])

  // Save search options to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('dictSearchOptions', JSON.stringify({
        searchMainWord, searchDefinition, searchVariants, wordMatch, diacriticMatch,
        filterWithPronunciation, filterNoPronunciation, filterWithAudio
      }))
    }
  }, [searchMainWord, searchDefinition, searchVariants, wordMatch, diacriticMatch, filterWithPronunciation, filterNoPronunciation, filterWithAudio])

  // Haitian Creole alphabet
  const HAITIAN_ALPHABET = ['A', 'B', 'Ch', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'R', 'S', 'T', 'U', 'V', 'W', 'Y', 'Z', '#']

  // Check if user is staff
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return
    }

    if (!isAuthenticated) {
      router.push('/login')
      return
    }

    if (!isStaff) {
      router.push('/')
      return
    }

    // User is authenticated and is staff - fetch data
    fetchStats()
    fetchPOSCategories()
    fetchSemanticCategories()

    // Restore last selected POS/Category from localStorage, or default to "ANY"
    const savedViewMode = localStorage.getItem('dictViewMode')
    const savedPOS = localStorage.getItem('dictSelectedPOS')
    const savedCat = localStorage.getItem('dictSelectedCategory')

    if (savedViewMode === 'category' && savedCat) {
      try {
        const cat = JSON.parse(savedCat)
        if (cat?.category) {
          setSelectedCategory(cat)
          fetchEntriesByCategory(cat.category, 1, _savedSearchOpts)
          return
        }
      } catch (e) { /* ignore corrupt data */ }
    }

    if (savedPOS) {
      try {
        const pos = JSON.parse(savedPOS)
        if (pos?.abbreviation) {
          setSelectedPOS(pos)
          fetchEntriesByPOS(pos.abbreviation, 1, _savedSearchOpts)
          return
        }
      } catch (e) { /* ignore corrupt data */ }
    }

    // Fallback: select "ANY"
    setSelectedPOS({ abbreviation: 'any', full_name: 'Any', count: 0 })
    fetchEntriesByPOS('any', 1, _savedSearchOpts)
  }, [isAuthenticated, isStaff, authLoading])

  // Re-fetch data when dictionary version changes
  useEffect(() => {
    if (!isAuthenticated || !isStaff || authLoading) return

    fetchStats()
    fetchPOSCategories()
    fetchSemanticCategories()

    // Re-fetch current entries with new version filter
    if (selectedPOS) {
      fetchEntriesByPOS(selectedPOS.abbreviation, 1)
    } else if (selectedCategory) {
      fetchEntriesByCategory(selectedCategory.category, 1)
    }
  }, [dictionaryVersion])

  // Listen for theme changes from parent window when embedded
  useEffect(() => {
    if (!isEmbedded) return

    const handleMessage = (event) => {
      // Verify message is from same origin for security
      if (event.origin !== window.location.origin) return

      // Handle theme change messages
      if (event.data && event.data.type === 'THEME_CHANGE') {
        setTheme(event.data.theme)
      }

      // Handle dismiss modals messages
      if (event.data && event.data.type === 'DISMISS_MODALS') {
        setEditingEntry(null)
        setShowAddModal(false)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [isEmbedded, setTheme])

  // Notify parent window of clicks when embedded
  useEffect(() => {
    if (!isEmbedded) return

    const handleClick = () => {
      // Notify parent that there was a click in the iframe
      if (window.parent) {
        window.parent.postMessage({ type: 'IFRAME_CLICK' }, window.location.origin)
      }
    }

    document.addEventListener('click', handleClick)

    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [isEmbedded])

  const fetchStats = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get(`/v1/dictionaries/haitian/stats/?dictionary_version=${dictionaryVersion}`)
      setStats(response.data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const fetchPOSCategories = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get(`/v1/dictionaries/haitian/pos_categories/?dictionary_version=${dictionaryVersion}`)
      setPOSCategories(response.data)
    } catch (error) {
      console.error('Error fetching POS categories:', error)
    }
  }

  const fetchSemanticCategories = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get(`/v1/dictionaries/haitian/categories/?dictionary_version=${dictionaryVersion}`)
      setSemanticCategories(response.data)
    } catch (error) {
      console.error('Error fetching semantic categories:', error)
    }
  }

  const fetchAvailableLetters = async (pos) => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get(`/v1/dictionaries/haitian/?pos=${pos}&page_size=1000&dictionary_version=${dictionaryVersion}`)

      // Extract first letters from entries
      const entries = response.data.results || response.data
      const letters = new Set()

      entries.forEach(entry => {
        if (entry.main_word && entry.main_word.length > 0) {
          const firstChar = entry.main_word.charAt(0)
          // Check if it starts with a digit
          if (/^[0-9]/.test(firstChar)) {
            letters.add('#')
          // Check if it starts with "Ch"
          } else if (entry.main_word.length > 1 && entry.main_word.substring(0, 2).toUpperCase() === 'CH') {
            letters.add('Ch')
          } else {
            letters.add(firstChar.toUpperCase())
          }
        }
      })

      // Filter to only Haitian alphabet letters and sort
      const availableLetters = HAITIAN_ALPHABET.filter(letter => letters.has(letter))
      setAvailableLetters(availableLetters)
    } catch (error) {
      console.error('[HaitianDict] Error fetching available letters:', error)
      setAvailableLetters([])
    }
  }

  const fetchEntriesByPOS = async (pos, page = 1, savedOpts = null) => {
    // Use savedOpts (from localStorage) if provided, otherwise use current state
    const _withPron = savedOpts?.filterWithPronunciation ?? filterWithPronunciation
    const _noPron = savedOpts?.filterNoPronunciation ?? filterNoPronunciation
    const _withAudio = savedOpts?.filterWithAudio ?? filterWithAudio

    // Stamp this fetch so a stale response can be discarded if a newer
    // fetch races past it. See comment on latestFetchIdRef above.
    const fetchId = ++latestFetchIdRef.current

    setLoading(true)
    try {
      const api = (await import('../services/api')).default

      // Build URL - don't filter by POS if "any" is selected
      let url = `/v1/dictionaries/haitian/?page=${page}&dictionary_version=${dictionaryVersion}`

      if (pos !== 'any') {
        url += `&pos=${pos}`
      }

      // Add pronunciation filters
      if (_withPron && !_noPron) {
        url += '&has_pronunciation=true'
      } else if (_noPron && !_withPron) {
        url += '&has_pronunciation=false'
      }
      // If both or neither are checked, don't filter by pronunciation

      // Filter by audio (entries with actual cached audio)
      if (_withAudio) {
        url += '&has_audio=true'
      }

      // Add search term with field filters. Reads debouncedSearchTerm
      // so the fetch matches what the user has actually stopped typing.
      if (debouncedSearchTerm) {
        const term = wordMatch ? `^${debouncedSearchTerm}$` : debouncedSearchTerm
        url += `&search=${encodeURIComponent(term)}`
        url += `&search_main_word=${searchMainWord}`
        url += `&search_definition=${searchDefinition}`
        url += `&search_variants=${searchVariants}`
        url += `&diacritic_match=${diacriticMatch}`
      }

      if (selectedLetter) {
        url += `&starts_with=${encodeURIComponent(selectedLetter)}`
      }

      console.log('[HaitianDict] Fetching entries from:', url)
      const response = await api.get(url)
      console.log('[HaitianDict] Response:', response.data)

      // Discard if a newer fetch has since started — its response will
      // set state instead, and we don't want to overwrite it with ours.
      if (fetchId !== latestFetchIdRef.current) {
        console.log('[HaitianDict] Discarding stale response for fetchId', fetchId, 'latest is', latestFetchIdRef.current)
        return
      }

      // Handle paginated response
      const data = response.data
      const entries = data.results || data

      setEntries(entries)
      setCurrentPage(page)
      setTotalCount(data.count || entries.length)
      setHasNext(!!data.next)
      setHasPrevious(!!data.previous)

      console.log('[HaitianDict] Setting entries:', entries.length, 'items')
      console.log('[HaitianDict] Pagination:', { page, total: data.count, hasNext: !!data.next, hasPrevious: !!data.previous })
    } catch (error) {
      // Same stale-guard on the error path.
      if (fetchId !== latestFetchIdRef.current) return
      console.error('[HaitianDict] Error fetching entries:', error)
      if (error.response) {
        console.error('[HaitianDict] Error response:', error.response.data)
        console.error('[HaitianDict] Error status:', error.response.status)
      }
      setEntries([])
      setTotalCount(0)
      setHasNext(false)
      setHasPrevious(false)
    } finally {
      // Only clear the spinner if this was the latest fetch; otherwise
      // the newer in-flight fetch will clear it when it completes.
      if (fetchId === latestFetchIdRef.current) {
        setLoading(false)
      }
    }
  }

  const fetchEntriesByCategory = async (category, page = 1, savedOpts = null) => {
    // Use savedOpts (from localStorage) if provided, otherwise use current state
    const _withPron = savedOpts?.filterWithPronunciation ?? filterWithPronunciation
    const _noPron = savedOpts?.filterNoPronunciation ?? filterNoPronunciation
    const _withAudio = savedOpts?.filterWithAudio ?? filterWithAudio

    // Stamp this fetch so a stale response can be discarded if a newer
    // fetch races past it. See comment on latestFetchIdRef above.
    const fetchId = ++latestFetchIdRef.current

    setLoading(true)
    try {
      const api = (await import('../services/api')).default

      // Build URL - don't filter by category if "all" is selected
      let url = `/v1/dictionaries/haitian/?page=${page}&dictionary_version=${dictionaryVersion}`

      if (category !== 'all') {
        url += `&category=${encodeURIComponent(category)}`
      }

      // Add pronunciation filters
      if (_withPron && !_noPron) {
        url += '&has_pronunciation=true'
      } else if (_noPron && !_withPron) {
        url += '&has_pronunciation=false'
      }
      // If both or neither are checked, don't filter by pronunciation

      // Filter by audio (entries with actual cached audio)
      if (_withAudio) {
        url += '&has_audio=true'
      }

      // Add search term with field filters. Reads debouncedSearchTerm
      // so the fetch matches what the user has actually stopped typing.
      if (debouncedSearchTerm) {
        const term = wordMatch ? `^${debouncedSearchTerm}$` : debouncedSearchTerm
        url += `&search=${encodeURIComponent(term)}`
        url += `&search_main_word=${searchMainWord}`
        url += `&search_definition=${searchDefinition}`
        url += `&search_variants=${searchVariants}`
        url += `&diacritic_match=${diacriticMatch}`
      }

      if (selectedLetter) {
        url += `&starts_with=${encodeURIComponent(selectedLetter)}`
      }

      console.log('[HaitianDict] Fetching entries from:', url)
      const response = await api.get(url)

      // Discard if a newer fetch has since started.
      if (fetchId !== latestFetchIdRef.current) {
        console.log('[HaitianDict] Discarding stale response for fetchId', fetchId, 'latest is', latestFetchIdRef.current)
        return
      }

      const data = response.data
      const entries = data.results || data

      setEntries(entries)
      setCurrentPage(page)
      setTotalCount(data.count || entries.length)
      setHasNext(!!data.next)
      setHasPrevious(!!data.previous)
    } catch (error) {
      if (fetchId !== latestFetchIdRef.current) return
      console.error('[HaitianDict] Error fetching entries:', error)
      setEntries([])
      setTotalCount(0)
      setHasNext(false)
      setHasPrevious(false)
    } finally {
      if (fetchId === latestFetchIdRef.current) {
        setLoading(false)
      }
    }
  }

  const savePronunciation = async (entryId, phonetic, ipa, notes) => {
    const api = (await import('../services/api')).default
    await api.post(`/v1/dictionaries/haitian/${entryId}/set_pronunciation/`, {
      entry_id: entryId,
      phonetic_spelling: phonetic,
      ipa_pronunciation: ipa || '',
      notes: notes || ''
    })
    // Refresh entries and stats
    if (selectedPOS) {
      fetchEntriesByPOS(selectedPOS.abbreviation)
    }
    fetchStats()
  }

  const removePronunciation = async (entryId) => {
    const handleConfirm = async () => {
      hideAlert()
      const api = (await import('../services/api')).default
      await api.delete(`/v1/dictionaries/haitian/${entryId}/remove_pronunciation/`)
      // Refresh entries and stats
      if (selectedPOS) {
        fetchEntriesByPOS(selectedPOS.abbreviation)
      }
      fetchStats()
    }

    showAlert(
      'The word will be sent as-is to TTS.',
      'Clear Pronunciation?',
      'warning',
      <>
        <button
          onClick={hideAlert}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 rounded-md transition-colors"
        >
          Clear
        </button>
      </>
    )
  }

  const clearAudio = (entryId) => {
    return new Promise((resolve) => {
      const handleConfirm = async () => {
        // Show progress indicator in the dialog
        showAlert('Clearing audio files from S3...', 'Clearing Audio', 'info',
          <button
            onClick={hideAlert}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 inline-flex items-center"
          >
            <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            OK
          </button>
        )
        try {
          const api = (await import('../services/api')).default
          await api.delete(`/v1/dictionaries/haitian/${entryId}/clear_audio/`)
          hideAlert()
          // Refresh entries and stats
          if (selectedPOS) {
            fetchEntriesByPOS(selectedPOS.abbreviation)
          }
          fetchStats()
        } catch (error) {
          console.error('[HaitianDict] Error clearing audio:', error)
          showAlert('Failed to clear audio. Please try again.', 'Error', 'error')
        } finally {
          resolve()
        }
      }

      const handleCancel = () => {
        hideAlert()
        resolve()
      }

      showAlert(
        'This will delete the cached audio files from S3. The pronunciation can be regenerated.',
        'Clear Audio?',
        'warning',
        <>
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 rounded-md transition-colors"
          >
            Clear Audio
          </button>
        </>
      )
    })
  }

  // Fetch sync status
  const fetchSyncStatus = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get('/v1/dictionaries/haitian/sync-status/')
      setSyncStatus(response.data)
    } catch (error) {
      console.error('Failed to fetch sync status:', error)
    }
  }

  // Handle sync button click
  const handleOpenSyncModal = () => {
    setShowSyncModal(true)
  }

  // Handle sync to Infer
  const handleSyncToInfer = async (webhookCallback) => {
    try {
      syncModalCallbackRef.current = webhookCallback

      const api = (await import('../services/api')).default
      const response = await api.post('/v1/dictionaries/haitian/sync-system/', {
        force: false
      })

      if (response.status !== 200 && response.status !== 202) {
        if (webhookCallback) {
          webhookCallback({
            event: 'haitian_dictionary_sync_completed',
            data: {
              status: 'error',
              error: 'Failed to start sync'
            }
          })
        }
        showAlert('Failed to start sync', 'Error', 'error')
      }
    } catch (error) {
      console.error('Failed to sync dictionary:', error)
      if (webhookCallback) {
        webhookCallback({
          event: 'haitian_dictionary_sync_completed',
          data: {
            status: 'error',
            error: 'Failed to connect to server'
          }
        })
      }
      showAlert('Failed to sync dictionary', 'Error', 'error')
    }
  }

  // Handle WebSocket updates for Haitian dictionary sync
  const handleHaitianDictionarySyncUpdate = (data) => {
    if (data.event && data.event.startsWith('haitian_')) {
      if (syncModalCallbackRef.current) {
        syncModalCallbackRef.current(data)
      }

      // Refresh stats after successful sync
      if (data.event === 'haitian_dictionary_sync_completed' && data.data?.status === 'success') {
        fetchStats()
        fetchSyncStatus()
      }

      // Refresh sync status when dictionary changes (category marked out of sync)
      if (data.event === 'haitian_dictionary_sync_status_changed') {
        fetchSyncStatus()
      }
    }
  }

  // Initialize WebSocket for dictionary sync updates
  const { isConnected: wsConnected } = useWebSocketUpdates(handleHaitianDictionarySyncUpdate)

  // Fetch sync status on mount
  useEffect(() => {
    if (isAuthenticated && isStaff) {
      fetchSyncStatus()
    }
  }, [isAuthenticated, isStaff])

  // 300ms debounce: mirror searchTerm → debouncedSearchTerm after the
  // user pauses typing. Cancels any pending mirror on new keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    if (selectedPOS) {
      setCurrentPage(1)
      fetchEntriesByPOS(selectedPOS.abbreviation, 1)
    } else if (selectedCategory) {
      setCurrentPage(1)
      fetchEntriesByCategory(selectedCategory.category, 1)
    }
  }, [debouncedSearchTerm, searchMainWord, searchDefinition, searchVariants, wordMatch, diacriticMatch, filterWithPronunciation, filterNoPronunciation, filterWithAudio, selectedLetter])

  // Don't render until auth is loaded and verified
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated || !isStaff) {
    return null
  }

  return (
    <div className={isEmbedded ? "" : "min-h-screen bg-gray-50 dark:bg-gray-900"}>
      <SEO
        title="Haitian Creole Dictionary — 28,000+ Words — CreoleCentric"
        description="Free Haitian Creole dictionary with 28,000+ entries, audio pronunciations, English translations, and usage examples. Search by word or browse by category."
        path="/haitian-dictionary"
      />
      {!isEmbedded && <MobileBackButton />}
      <div className={isEmbedded ? "px-4 py-4" : "max-w-7xl mx-auto px-4 py-8"}>
        {/* Header */}
        {!isEmbedded && (
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Haitian Creole Dictionary (System {dictionaryVersion.toUpperCase()})
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Staff-only dictionary management and pronunciation editing
            </p>
          </div>
        )}

        {/* Statistics */}
        {stats && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Dictionary Statistics
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={handleOpenSyncModal}
                  className={`px-4 py-2 text-white rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    syncStatus?.out_of_sync > 0
                      ? 'bg-red-600 hover:bg-red-700 animate-pulse'
                      : 'bg-yellow-600 hover:bg-yellow-700'
                  }`}
                  title={syncStatus?.out_of_sync > 0
                    ? `${syncStatus.out_of_sync} categories need syncing`
                    : "Sync dictionary to Engine"}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>
                  {syncStatus?.out_of_sync > 0 ? 'Sync Required' : 'Sync to Engine'}
                </button>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <span className="text-lg">+</span>
                  Add New Word
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                title="Total Entries"
                value={stats.total_entries.toLocaleString()}
                color="blue"
              />
              <StatCard
                title="With Pronunciation"
                value={stats.entries_with_pronunciation.toLocaleString()}
                color="green"
              />
              <StatCard
                title="Coverage"
                value={`${stats.pronunciation_coverage}%`}
                color="purple"
              />
              <StatCard
                title="Without Pronunciation"
                value={stats.entries_without_pronunciation.toLocaleString()}
                color="orange"
              />
            </div>
          </div>
        )}

        {/* View Mode Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-4">
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                setViewMode('pos')
                setSelectedCategory(null)
                setEntries([])
              }}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                viewMode === 'pos'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Parts of Speech
            </button>
            <button
              onClick={() => {
                setViewMode('category')
                setSelectedPOS(null)
                setEntries([])
              }}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                viewMode === 'category'
                  ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Categories
            </button>
          </div>
        </div>

        {/* POS Categories */}
        {viewMode === 'pos' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              Parts of Speech
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
              {/* ANY option as first button */}
              <button
                onClick={() => {
                  setSelectedPOS({ abbreviation: 'any', full_name: 'Any', count: stats?.total_entries || 0 })
                  setCurrentPage(1)
                  setSelectedLetter(null)
                  fetchEntriesByPOS('any', 1)
                }}
                className={`p-3 rounded-lg text-center transition-colors ${
                  selectedPOS?.abbreviation === 'any'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <div className="font-bold">ANY</div>
                <div className="text-sm opacity-75">{stats?.total_entries || 0}</div>
              </button>

              {/* Regular POS categories */}
              {posCategories.map((pos) => (
                <button
                  key={pos.abbreviation}
                  onClick={() => {
                    setSelectedPOS(pos)
                    setCurrentPage(1)
                    setSelectedLetter(null)
                    fetchAvailableLetters(pos.abbreviation)
                    fetchEntriesByPOS(pos.abbreviation, 1)
                  }}
                  className={`p-3 rounded-lg text-center transition-colors ${
                    selectedPOS?.abbreviation === pos.abbreviation
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="font-bold">{pos.abbreviation}</div>
                  <div className="text-sm opacity-75">{pos.count}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Semantic Categories */}
        {viewMode === 'category' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              Semantic Categories
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* All Words option as first button */}
              <button
                onClick={() => {
                  setSelectedCategory({ category: 'all', count: stats?.total_entries || 0 })
                  setCurrentPage(1)
                  setSelectedLetter(null)
                  fetchEntriesByCategory('all', 1)
                }}
                className={`p-3 rounded-lg text-left transition-colors ${
                  selectedCategory?.category === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <div className="font-semibold text-sm">All Words</div>
                <div className="text-xs opacity-75">{stats?.total_entries || 0} words</div>
              </button>

              {/* Regular semantic categories */}
              {semanticCategories.map((cat) => (
                <button
                  key={cat.category}
                  onClick={() => {
                    setSelectedCategory(cat)
                    setCurrentPage(1)
                    setSelectedLetter(null)
                    fetchEntriesByCategory(cat.category, 1)
                  }}
                  className={`p-3 rounded-lg text-left transition-colors ${
                    selectedCategory?.category === cat.category
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="font-semibold text-sm">{cat.category}</div>
                  <div className="text-xs opacity-75">{cat.count} words</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filters and Search */}
        {(selectedPOS || selectedCategory) && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search entries..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                  Tip: Use <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px]">^word</code> to match start,
                  <code className="ml-1 px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px]">word$</code> to match end,
                  <code className="ml-1 px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px]">^word$</code> for exact match
                </div>
              </div>
              <div className="flex flex-col gap-2 bg-gray-50 dark:bg-gray-900 px-3 py-2 rounded-lg text-xs">
                <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Search in:</span>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={searchMainWord} onChange={(e) => setSearchMainWord(e.target.checked)} className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700" />
                    <span className="text-gray-700 dark:text-gray-300">Word</span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={searchDefinition} onChange={(e) => setSearchDefinition(e.target.checked)} className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700" />
                    <span className="text-gray-700 dark:text-gray-300">Definition</span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={searchVariants} onChange={(e) => setSearchVariants(e.target.checked)} className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700" />
                    <span className="text-gray-700 dark:text-gray-300">Variants</span>
                  </label>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Match:</span>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={wordMatch} onChange={(e) => setWordMatch(e.target.checked)} className="w-3.5 h-3.5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700" />
                    <span className="text-gray-700 dark:text-gray-300">Word Match</span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer" title="When enabled, diacritics must match exactly (e.g. 'lajes' won't match 'lajès')">
                    <input type="checkbox" checked={diacriticMatch} onChange={(e) => setDiacriticMatch(e.target.checked)} className="w-3.5 h-3.5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700" />
                    <span className="text-gray-700 dark:text-gray-300">Diacritic Match</span>
                  </label>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Pronunciation:</span>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={filterWithPronunciation} onChange={(e) => setFilterWithPronunciation(e.target.checked)} className="w-3.5 h-3.5 text-green-600 border-gray-300 rounded focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700" />
                    <span className="text-gray-700 dark:text-gray-300">With</span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={filterNoPronunciation} onChange={(e) => setFilterNoPronunciation(e.target.checked)} className="w-3.5 h-3.5 text-orange-600 border-gray-300 rounded focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-700" />
                    <span className="text-gray-700 dark:text-gray-300">Without</span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={filterWithAudio} onChange={(e) => setFilterWithAudio(e.target.checked)} className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700" />
                    <span className="text-gray-700 dark:text-gray-300">Audio</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Alphabet Filter */}
        {(selectedPOS || selectedCategory) && availableLetters.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Filter by letter:</h3>
              {selectedLetter && (
                <button
                  onClick={() => setSelectedLetter(null)}
                  className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {HAITIAN_ALPHABET.map((letter) => {
                const isAvailable = availableLetters.includes(letter)
                const isSelected = selectedLetter === letter
                return (
                  <button
                    key={letter}
                    onClick={() => isAvailable && setSelectedLetter(isSelected ? null : letter)}
                    disabled={!isAvailable}
                    className={`px-3 py-2 text-sm font-medium rounded transition-colors ${
                      isSelected
                        ? 'bg-blue-600 text-white'
                        : isAvailable
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                        : 'bg-gray-50 dark:bg-gray-900 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                    }`}
                  >
                    {letter}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Entries List */}
        {(selectedPOS || selectedCategory) && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Entries: {selectedPOS
                  ? (selectedPOS.full_name || selectedPOS.abbreviation)
                  : (selectedCategory?.category === 'all' ? 'All Words' : selectedCategory?.category)
                } ({totalCount > 0 ? totalCount : entries.length} total)
              </h2>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <div className="p-8 text-center text-gray-600 dark:text-gray-400">
                  Loading...
                </div>
              ) : entries.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  No entries found
                </div>
              ) : (
                entries.map((entry) => (
                  <EntryRow
                    key={entry.id}
                    entry={entry}
                    searchTerm={searchTerm}
                    onEdit={setEditingEntry}
                    onRemovePronunciation={removePronunciation}
                    onClearAudio={clearAudio}
                  />
                ))
              )}
            </div>

            {/* Pagination Controls */}
            {!loading && entries.length > 0 && (hasPrevious || hasNext) && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Showing {((currentPage - 1) * 100) + 1} to {Math.min(currentPage * 100, totalCount)} of {totalCount} entries
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => selectedPOS
                        ? fetchEntriesByPOS(selectedPOS.abbreviation, currentPage - 1)
                        : fetchEntriesByCategory(selectedCategory.category, currentPage - 1)
                      }
                      disabled={!hasPrevious}
                      className="px-4 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <div className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Page {currentPage}
                    </div>
                    <button
                      onClick={() => selectedPOS
                        ? fetchEntriesByPOS(selectedPOS.abbreviation, currentPage + 1)
                        : fetchEntriesByCategory(selectedCategory.category, currentPage + 1)
                      }
                      disabled={!hasNext}
                      className="px-4 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edit Modal */}
        {editingEntry && (
          <EditPronunciationModal
            key={`${editingEntry.id}-${editingEntry.pronunciation?.phonetic_spelling || 'new'}`}
            entry={editingEntry}
            onClose={() => setEditingEntry(null)}
            onSave={savePronunciation}
          />
        )}

        {/* Add New Word Modal */}
        {showAddModal && (
          <AddNewDictionaryEntryModal
            apiEndpoint="/v1/dictionaries/haitian/"
            onClose={() => setShowAddModal(false)}
            onSuccess={() => {
              setShowAddModal(false)
              fetchStats()
              fetchSemanticCategories()
              // Refresh current view if applicable
              if (selectedPOS) {
                fetchEntriesByPOS(selectedPOS.abbreviation, currentPage)
              } else if (selectedCategory) {
                fetchEntriesByCategory(selectedCategory.category, currentPage)
              }
            }}
          />
        )}

        {/* Sync to Engine Modal */}
        <HaitianDictionarySyncModal
          isOpen={showSyncModal}
          onClose={() => {
            setShowSyncModal(false)
            syncModalCallbackRef.current = null
            fetchSyncStatus() // Refresh sync status after modal closes
          }}
          onSync={handleSyncToInfer}
          isUserDict={false}
        />

        {/* Custom Alert Modal */}
        <AlertComponent />
      </div>
    </div>
  )
}

function StatCard({ title, value, color = 'blue' }) {
  const colors = {
    blue: 'border-blue-500',
    green: 'border-green-500',
    purple: 'border-purple-500',
    orange: 'border-orange-500'
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 ${colors[color]}`}>
      <div className="text-sm text-gray-600 dark:text-gray-400">{title}</div>
      <div className="text-2xl font-bold mt-2 text-gray-900 dark:text-white">{value}</div>
    </div>
  )
}

// Helper function to highlight search term in text
function highlightSearchTerm(text, searchTerm) {
  if (!searchTerm || !text) return text

  // Strip regex anchors for highlighting purposes
  let cleanedTerm = searchTerm
    .replace(/^\^/, '')  // Remove start anchor
    .replace(/\$$/, '')  // Remove end anchor

  if (!cleanedTerm) return text

  try {
    // Escape special regex characters except for already-stripped anchors
    const escapedTerm = cleanedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedTerm})`, 'gi')

    const parts = text.split(regex)

    if (parts.length === 1) return text // No match found

    return parts.map((part, i) => {
      if (part.toLowerCase() === cleanedTerm.toLowerCase()) {
        return <mark key={i} className="bg-yellow-200 dark:bg-yellow-700 px-0.5 rounded">{part}</mark>
      }
      return part
    })
  } catch (e) {
    // If regex fails, just return original text
    return text
  }
}

function EntryRow({ entry, searchTerm, onEdit, onRemovePronunciation, onClearAudio }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [fullEntry, setFullEntry] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)

  const toggleDetails = async () => {
    if (!isExpanded && !fullEntry) {
      // Fetch full entry details
      setLoadingDetails(true)
      try {
        const api = (await import('../services/api')).default
        const response = await api.get(`/v1/dictionaries/haitian/${entry.id}/`)
        setFullEntry(response.data)
      } catch (error) {
        console.error('[HaitianDict] Error fetching entry details:', error)
      } finally {
        setLoadingDetails(false)
      }
    }
    setIsExpanded(!isExpanded)
  }

  return (
    <div className={`p-4 transition-colors ${isExpanded
      ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
    }`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {highlightSearchTerm(entry.main_word, searchTerm)}
            </h3>
            {entry.definite_article && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                ({entry.definite_article})
              </span>
            )}
            {entry.homonym_number && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                #{entry.homonym_number}
              </span>
            )}
            {entry.has_pronunciation ? (
              <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs rounded">
                ✓ Pronunciation
              </span>
            ) : (
              <span className="px-2 py-1 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 text-xs rounded">
                No Pronunciation
              </span>
            )}
          </div>
          {entry.pos_list && entry.pos_list.length > 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              POS: {entry.pos_list.join(', ')}
            </div>
          )}
          {entry.definition_preview && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 [&_i]:italic [&_b]:font-bold">
              {searchTerm ? (
                highlightSearchTerm(entry.definition_preview.replace(/<[^>]+>/g, ''), searchTerm)
              ) : (
                <span dangerouslySetInnerHTML={{ __html: entry.definition_preview }} />
              )}
              {entry.see_also_info && entry.see_also_info.pos_list && entry.see_also_info.pos_list.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                  {entry.see_also_info.pos_list.join(', ')}
                </span>
              )}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={toggleDetails}
            disabled={loadingDetails}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded disabled:opacity-50"
          >
            {loadingDetails ? 'Loading...' : (isExpanded ? 'Hide' : 'Details')}
          </button>
          <button
            onClick={() => onEdit(entry)}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded"
          >
            {entry.has_pronunciation ? 'Edit' : 'Add'} Pronunciation
          </button>
          {entry.has_audio && (
            <button
              onClick={() => onClearAudio(entry.id)}
              className="px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded"
              title="Clear cached audio files from S3"
            >
              Clear Audio
            </button>
          )}
          {entry.has_pronunciation && (
            <button
              onClick={() => onRemovePronunciation(entry.id)}
              className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded"
              title="Clear pronunciation"
            >
              Clear Pronunciation
            </button>
          )}
        </div>
      </div>

      {isExpanded && fullEntry && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
          {/* Definitions with POS buttons */}
          {fullEntry.definition_segments && fullEntry.definition_segments.length > 0 ? (
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Definitions:</h4>
              <div className="space-y-2">
                {fullEntry.definition_segments.map((segment, idx) => (
                  <div key={idx} className="flex flex-wrap items-start gap-2">
                    {/* POS Button */}
                    {segment.pos_abbrev && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 cursor-help"
                        title={segment.pos_full || segment.pos_abbrev}
                      >
                        {segment.pos_abbrev}
                      </span>
                    )}
                    {/* Definition Text */}
                    <span
                      className="text-sm text-gray-700 dark:text-gray-300 flex-1 [&_i]:italic [&_b]:font-bold"
                      dangerouslySetInnerHTML={{ __html: segment.text }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : fullEntry.definitions && fullEntry.definitions.length > 0 ? (
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Definitions:</h4>
              <ul className="list-disc list-inside space-y-1">
                {fullEntry.definitions.map((def) => (
                  <li key={def.id} className="text-sm text-gray-700 dark:text-gray-300 [&_i]:italic [&_b]:font-bold">
                    <span dangerouslySetInnerHTML={{ __html: def.definition_text }} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Variants */}
          {fullEntry.variants && fullEntry.variants.length > 0 && (
            <div>
              <span className="font-semibold text-gray-900 dark:text-white">Variants:</span>{' '}
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {fullEntry.variants.map(v => v.variant_word).join(', ')}
              </span>
            </div>
          )}

          {/* Usage Examples */}
          {fullEntry.usage_examples && fullEntry.usage_examples.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Usage Examples:</h4>
              <div className="space-y-2">
                {fullEntry.usage_examples.map((usage) => (
                  <div key={usage.id} className="text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded">
                    <div className="text-gray-900 dark:text-white font-medium [&_i]:italic [&_b]:font-bold" dangerouslySetInnerHTML={{ __html: usage.creole_example }} />
                    <div className="text-gray-600 dark:text-gray-400 italic [&_i]:italic [&_b]:font-bold" dangerouslySetInnerHTML={{ __html: usage.english_translation }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pronunciation Details */}
          {fullEntry.pronunciation && (
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Pronunciation:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Phonetic:</span>{' '}
                  <span className="text-gray-700 dark:text-gray-300">{fullEntry.pronunciation.phonetic_spelling}</span>
                </div>
                {fullEntry.pronunciation.ipa_pronunciation && (
                  <div>
                    <span className="font-semibold text-gray-900 dark:text-white">IPA:</span>{' '}
                    <span className="text-gray-700 dark:text-gray-300">{fullEntry.pronunciation.ipa_pronunciation}</span>
                  </div>
                )}
                {fullEntry.pronunciation.notes && (
                  <div className="md:col-span-2">
                    <span className="font-semibold text-gray-900 dark:text-white">Notes:</span>{' '}
                    <span className="text-gray-700 dark:text-gray-300">{fullEntry.pronunciation.notes}</span>
                  </div>
                )}
                {fullEntry.pronunciation.updated_by && (
                  <div className="md:col-span-2 text-xs text-gray-500 dark:text-gray-400">
                    Updated by {fullEntry.pronunciation.updated_by} at{' '}
                    {new Date(fullEntry.pronunciation.updated_at).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* References */}
          {fullEntry.references && fullEntry.references.length > 0 && (
            <div>
              <span className="font-semibold text-gray-900 dark:text-white">References:</span>{' '}
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {fullEntry.references.map(r => r.reference_code).join(', ')}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function EditPronunciationModal({ entry, onClose, onSave }) {
  const [phonetic, setPhonetic] = useState(
    entry.pronunciation?.phonetic_spelling || entry.main_word
  )
  const [ipa, setIPA] = useState(entry.pronunciation?.ipa_pronunciation || '')
  const [notes, setNotes] = useState(entry.pronunciation?.notes || '')
  const [saving, setSaving] = useState(false)
  const [generatingIPA, setGeneratingIPA] = useState(false)
  const [error, setError] = useState('')

  // Detect IPA characters
  const ipaPattern = /[\u00E6\u00F0\u00F8\u014B\u0188\u0199\u01A5\u01AD\u0250-\u02FF\u03B2\u03B8\u03C6\u03C7\u1D00-\u1DBF]/
  const phoneticHasIPA = ipaPattern.test(phonetic)

  // When IPA is filled and phonetic is empty, disable phonetic
  const phoneticDisabled = ipa.trim() && !phonetic.trim()

  const handleGenerateIPA = async () => {
    const text = phonetic.trim() || entry.main_word
    if (!text) return
    setGeneratingIPA(true)
    setError('')
    try {
      const api = (await import('../services/api')).default
      const res = await api.post('/v1/dictionaries/generate-ipa/', { text })
      if (res.data.ipa) {
        setIPA(res.data.ipa)
      }
    } catch (err) {
      console.error('Failed to generate IPA:', err)
      setError(err.response?.data?.error || 'Failed to generate IPA')
    } finally {
      setGeneratingIPA(false)
    }
  }

  const handleSave = async () => {
    if (phoneticHasIPA) {
      setError('Phonetic spelling contains IPA characters. Use the IPA field for IPA notation.')
      return
    }

    setSaving(true)
    setError('')
    try {
      await onSave(entry.id, phonetic, ipa, notes)
      onClose()
    } catch (error) {
      console.error('Error saving pronunciation:', error)
      setError(error.response?.data?.detail || 'Failed to save pronunciation')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
          {entry.pronunciation ? 'Edit' : 'Add'} Pronunciation: {entry.main_word}
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">
              Phonetic Spelling <span className="text-xs text-gray-400 font-normal">(Optional)</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={phonetic}
                onChange={(e) => setPhonetic(e.target.value)}
                disabled={phoneticDisabled}
                className={`flex-1 px-4 py-2 border rounded-lg dark:bg-gray-700 dark:text-white ${phoneticDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${phoneticHasIPA ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                placeholder={phoneticDisabled ? 'Clear IPA to edit phonetic' : 'Enter phonetic spelling'}
                autoFocus={!phoneticDisabled}
              />
              <button
                onClick={handleGenerateIPA}
                disabled={generatingIPA || !phonetic.trim()}
                className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg disabled:opacity-50 whitespace-nowrap"
                title="Generate IPA from phonetic spelling"
              >
                {generatingIPA ? 'Generating...' : 'Generate IPA'}
              </button>
            </div>
            {phoneticHasIPA ? (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                IPA characters detected. Use the IPA field below for IPA notation.
              </p>
            ) : phoneticDisabled ? (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                Clear IPA field to enable phonetic spelling.
              </p>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Used by the TTS engine. If both are empty, Infer uses default pronunciation.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">
              IPA <span className="text-xs text-gray-400 font-normal">(Optional)</span>
            </label>
            <input
              type="text"
              value={ipa}
              onChange={(e) => setIPA(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              placeholder="Enter IPA notation (auto-generated from phonetic if empty)"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              International Phonetic Alphabet. Auto-generated from phonetic on save if left empty.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">
              Notes <span className="text-xs text-gray-400 font-normal">(Optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              placeholder="Add any notes about this pronunciation"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={phoneticHasIPA || saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Pronunciation'}
          </button>
        </div>
      </div>
    </div>
  )
}

